import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShoppingBag } from 'lucide-react'
import { useCart } from '../../context/CartContext'
import WaitlistForm from './WaitlistForm'

export default function ProductCard({ product }) {
  const { addToCart } = useCart()

  const handleAddToCart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    addToCart(product)
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  // Handle both camelCase (static data) and snake_case (Supabase) field names
  const comparePrice = product.compare_price || product.comparePrice
  const shortDescription = product.short_description || product.shortDescription
  const comingSoon = product.coming_soon || product.comingSoon

  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      className="group"
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-square bg-pp-sand/30 rounded-2xl overflow-hidden mb-4">
          {/* Product Image */}
          {product.images && product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={`absolute inset-0 flex items-center justify-center bg-pp-sand/30 ${product.images && product.images[0] ? 'hidden' : ''}`}
          >
            <span className="text-pp-stone text-sm">No Image</span>
          </div>

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {comingSoon && (
              <span className="inline-block px-3 py-1 bg-pp-charcoal text-white text-xs font-medium rounded-full">
                Coming Soon
              </span>
            )}
            {product.bestseller && !comingSoon && (
              <span className="inline-block px-3 py-1 bg-pp-terracotta text-white text-xs font-medium rounded-full">
                Bestseller
              </span>
            )}
            {comparePrice && !comingSoon && (
              <span className="inline-block px-3 py-1 bg-pp-sage text-white text-xs font-medium rounded-full">
                Sale
              </span>
            )}
          </div>

          {/* Quick Add Button - hide for coming soon products */}
          {!comingSoon && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ scale: 1.05 }}
              className="absolute bottom-3 right-3 p-3 bg-white rounded-full shadow-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              onClick={handleAddToCart}
              aria-label="Add to cart"
            >
              <ShoppingBag className="w-5 h-5 text-pp-charcoal" />
            </motion.button>
          )}

          {/* Waitlist form for coming soon products */}
          {comingSoon && (
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-xs text-pp-stone mb-2 text-center">Be the first to get yours!</p>
              <WaitlistForm productId={product.id} compact />
            </div>
          )}
        </div>

        <div className="space-y-1">
          <h3 className="font-display text-lg text-pp-ink group-hover:text-pp-terracotta transition-colors">
            {product.name}
          </h3>
          <p className="text-pp-stone text-sm line-clamp-1">
            {shortDescription}
          </p>
          <div className="flex items-center gap-2 pt-1">
            <span className="font-body font-medium text-pp-ink">
              {formatPrice(product.price)}
            </span>
            {comparePrice && (
              <span className="text-pp-stone text-sm line-through">
                {formatPrice(comparePrice)}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  )
}
